import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FaDownload, FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin, FaGlobe, FaReact, FaNodeJs, FaWordpress, FaHtml5, FaCss3Alt, FaBootstrap, FaJs, FaSearch, FaLink, FaCogs, FaDatabase, FaCode, FaServer } from 'react-icons/fa';
import { SiMongodb, SiExpress, SiTailwindcss, SiElementor, SiWoo } from 'react-icons/si';
import API from '../api/axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const iconMap = {
  'React.js': <FaReact />, 'Node.js': <FaNodeJs />, 'Express.js': <SiExpress />,
  'MongoDB': <SiMongodb />, 'HTML5': <FaHtml5 />, 'CSS3': <FaCss3Alt />,
  'Bootstrap': <FaBootstrap />, 'Tailwind CSS': <SiTailwindcss />, 'JavaScript': <FaJs />,
  'WordPress': <FaWordpress />, 'Elementor': <SiElementor />, 'WooCommerce': <SiWoo />,
  'On-Page SEO': <FaSearch />, 'Off-Page SEO': <FaLink />, 'Technical SEO': <FaCogs />,
  'Link Building': <FaLink />, 'REST APIs': <FaServer />, 'Git & GitHub': <FaCode />,
};

const defaultSkillsList = [
  'HTML5', 'CSS3', 'JavaScript', 'React.js', 'Bootstrap', 'Tailwind CSS',
  'Node.js', 'Express.js', 'REST APIs', 'MongoDB',
  'WordPress', 'Elementor', 'WooCommerce',
  'On-Page SEO', 'Off-Page SEO', 'Link Building', 'Technical SEO'
];

const ResumePage = () => {
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [projects, setProjects] = useState([]);
  const resumeRef = useRef();

  useEffect(() => {
    Promise.allSettled([API.get('/profile'), API.get('/skills'), API.get('/experience'), API.get('/projects')])
      .then(([p, s, e, pr]) => {
        if (p.status === 'fulfilled') setProfile(p.value.data);
        if (s.status === 'fulfilled') setSkills(s.value.data);
        if (e.status === 'fulfilled') setExperiences(e.value.data);
        if (pr.status === 'fulfilled') setProjects(pr.value.data);
      });
  }, []);

  const downloadPDF = () => {
    const content = resumeRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>${profile?.name || 'Asif'} - Resume</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a2e; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { font-size: 28px; color: #6c63ff; margin-bottom: 5px; }
        h2 { font-size: 18px; color: #6c63ff; border-bottom: 2px solid #6c63ff; padding-bottom: 5px; margin: 25px 0 12px; text-transform: uppercase; letter-spacing: 1px; }
        h3 { font-size: 15px; margin-bottom: 3px; }
        p { font-size: 13px; color: #444; }
        .subtitle { font-size: 14px; color: #666; margin-bottom: 8px; }
        .contact-line { font-size: 12px; color: #666; margin-bottom: 15px; }
        .contact-line span { margin-right: 15px; }
        .section { margin-bottom: 5px; }
        .exp-item { margin-bottom: 15px; }
        .exp-header { display: flex; justify-content: space-between; align-items: baseline; }
        .date { color: #888; font-size: 12px; font-style: italic; }
        .skills-grid { display: flex; flex-wrap: wrap; gap: 8px; }
        .skill-tag { padding: 4px 12px; background: #f0eeff; color: #6c63ff; border-radius: 15px; font-size: 12px; font-weight: 500; }
        .project-item { margin-bottom: 10px; }
        .project-item a { color: #6c63ff; text-decoration: none; font-size: 12px; }
        .divider { border: none; border-top: 1px solid #eee; margin: 10px 0; }
        @media print { body { padding: 20px; } }
      </style></head><body>
        <h1>${profile?.name || 'Asif'}</h1>
        <p class="subtitle">${profile?.title || 'Full Stack Web Developer'}</p>
        <div class="contact-line">
          ${profile?.email ? `<span>📧 ${profile.email}</span>` : ''}
          ${profile?.phone ? `<span>📱 ${profile.phone}</span>` : ''}
          ${profile?.location ? `<span>📍 ${profile.location}</span>` : ''}
          ${profile?.social?.github ? `<span>🔗 ${profile.social.github}</span>` : ''}
        </div>

        <div class="section">
          <h2>Summary</h2>
          <p>${profile?.bio || 'Experienced Full Stack Web Developer specialized in MERN Stack, WordPress, and SEO. I build modern, scalable web applications and provide comprehensive digital services to help businesses grow online.'}</p>
        </div>

        <div class="section">
          <h2>Skills</h2>
          <div class="skills-grid">
            ${(skills.length > 0 ? skills.map(s => s.name) : defaultSkillsList).map(s => `<span class="skill-tag">${s}</span>`).join('')}
          </div>
        </div>

        <div class="section">
          <h2>Experience</h2>
          ${(experiences.length > 0 ? experiences : [
            { position: 'Full Stack Developer', company: 'Freelance', startDate: '2022', endDate: 'Present', description: 'Building custom MERN stack applications, WordPress sites, and providing SEO services.' },
            { position: 'Frontend Developer', company: 'Web Agency', startDate: '2021', endDate: '2022', description: 'Developed responsive websites using React.js, HTML/CSS, Bootstrap.' }
          ]).map(e => `
            <div class="exp-item">
              <div class="exp-header"><h3>${e.position} — ${e.company}</h3><span class="date">${e.startDate} - ${e.endDate || 'Present'}</span></div>
              <p>${e.description}</p>
            </div>
          `).join('')}
        </div>

        ${projects.length > 0 ? `
        <div class="section">
          <h2>Projects</h2>
          ${projects.slice(0, 5).map(p => `
            <div class="project-item">
              <h3>${p.title}</h3>
              <p>${p.description?.substring(0, 150) || ''}</p>
              ${p.liveUrl ? `<a href="${p.liveUrl}">🔗 Live Demo</a>` : ''} ${p.githubUrl ? `<a href="${p.githubUrl}"> | 📂 GitHub</a>` : ''}
            </div>
          `).join('')}
        </div>` : ''}

        <div class="section">
          <h2>Services</h2>
          <div class="skills-grid">
            ${['MERN Stack Development', 'WordPress Development', 'WooCommerce Store', 'SEO Optimization', 'React.js Frontend', 'API Development', 'Website Speed Optimization', 'UI/UX to Code'].map(s => `<span class="skill-tag">${s}</span>`).join('')}
          </div>
        </div>
      </body></html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const skillNames = skills.length > 0 ? skills.map(s => s.name) : defaultSkillsList;

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: '80px' }}>
        {/* Banner */}
        <section style={{ background: 'var(--gradient-hero)', padding: '80px 0 60px', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '10px' }}>My <span style={{ background: 'var(--gradient-1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Resume</span></h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', marginBottom: '25px' }}>View my qualifications and download my resume</p>
            <button onClick={downloadPDF} className="btn btn-primary" style={{ fontSize: '1.1rem', padding: '14px 35px' }}>
              <FaDownload /> Download Resume (PDF)
            </button>
          </motion.div>
        </section>

        {/* Resume Preview */}
        <section className="section" style={{ background: 'var(--bg-dark)' }}>
          <div className="container" style={{ maxWidth: '900px' }}>
            <div ref={resumeRef} className="admin-card" style={{ padding: '40px' }}>
              {/* Header */}
              <div style={{ borderBottom: '3px solid var(--primary)', paddingBottom: '20px', marginBottom: '25px' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '5px' }}>{profile?.name || 'Asif'}</h1>
                <p style={{ color: 'var(--primary)', fontSize: '1.1rem', marginBottom: '10px' }}>{profile?.title || 'Full Stack Web Developer'}</p>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  {profile?.email && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaEnvelope /> {profile.email}</span>}
                  {profile?.phone && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaPhone /> {profile.phone}</span>}
                  {profile?.location && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaMapMarkerAlt /> {profile.location}</span>}
                  {profile?.social?.github && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaGithub /> GitHub</span>}
                  {profile?.social?.linkedin && <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><FaLinkedin /> LinkedIn</span>}
                </div>
              </div>

              {/* Summary */}
              <div style={{ marginBottom: '25px' }}>
                <h2 style={{ fontSize: '1.2rem', color: 'var(--primary)', borderBottom: '1px solid var(--border)', paddingBottom: '5px', marginBottom: '10px' }}>PROFESSIONAL SUMMARY</h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                  {profile?.bio || 'Experienced Full Stack Web Developer specialized in MERN Stack (MongoDB, Express.js, React.js, Node.js), WordPress Development, and SEO. Passionate about building modern, scalable web applications and providing comprehensive digital services.'}
                </p>
              </div>

              {/* Skills */}
              <div style={{ marginBottom: '25px' }}>
                <h2 style={{ fontSize: '1.2rem', color: 'var(--primary)', borderBottom: '1px solid var(--border)', paddingBottom: '5px', marginBottom: '15px' }}>SKILLS & TECHNOLOGIES</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {skillNames.map((name, i) => (
                    <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'rgba(108,99,255,0.1)', borderRadius: '20px', border: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--primary)', fontSize: '0.9rem' }}>{iconMap[name] || <FaCode />}</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: '500' }}>{name}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Experience */}
              <div style={{ marginBottom: '25px' }}>
                <h2 style={{ fontSize: '1.2rem', color: 'var(--primary)', borderBottom: '1px solid var(--border)', paddingBottom: '5px', marginBottom: '15px' }}>EXPERIENCE</h2>
                {(experiences.length > 0 ? experiences : [
                  { _id: '1', position: 'Full Stack Developer', company: 'Freelance', startDate: '2022', endDate: 'Present', description: 'Building custom MERN stack apps, WordPress sites, and SEO services for clients globally.' },
                  { _id: '2', position: 'Frontend Developer', company: 'Web Agency', startDate: '2021', endDate: '2022', description: 'Developed responsive websites using React.js, HTML/CSS, and Bootstrap.' }
                ]).map((exp, i) => (
                  <div key={exp._id || i} style={{ marginBottom: '15px', paddingLeft: '15px', borderLeft: '3px solid var(--primary)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: '1rem' }}>{exp.position} <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}>@ {exp.company}</span></h3>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{exp.startDate} - {exp.endDate || 'Present'}</span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '5px' }}>{exp.description}</p>
                  </div>
                ))}
              </div>

              {/* Services */}
              <div>
                <h2 style={{ fontSize: '1.2rem', color: 'var(--primary)', borderBottom: '1px solid var(--border)', paddingBottom: '5px', marginBottom: '15px' }}>SERVICES OFFERED</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {['MERN Stack Development', 'WordPress Development', 'WooCommerce E-Commerce', 'React.js Frontend', 'Node.js Backend & APIs', 'On-Page SEO', 'Off-Page SEO & Link Building', 'Technical SEO'].map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      <span style={{ color: 'var(--accent)' }}>▸</span> {s}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Download Button Bottom */}
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <button onClick={downloadPDF} className="btn btn-primary" style={{ fontSize: '1.1rem', padding: '14px 40px' }}>
                <FaDownload /> Download as PDF
              </button>
            </div>
          </div>
        </section>
      </div>
      <Footer profile={profile} />
    </>
  );
};

export default ResumePage;
